import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { select, Store } from '@ngrx/store';
import { filter, map, Observable, Subscription, take, tap } from 'rxjs';

import { BookingHeaderSummary } from '@fcom/common/interfaces';
import { TripType } from '@fcom/core/interfaces';
import { ButtonSize, ButtonTheme } from '@fcom/ui-components';
import { hasOrderData } from '@fcom/common-booking';
import { getTripTypeFromBookingLocationCodes } from '@fcom/common-booking/utils/common-booking.utils';
import { AppState, ConfigService, RootPaths } from '@fcom/core';
import { unsubscribe } from '@fcom/core/utils';
import { LanguageService } from '@fcom/ui-translate';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';

import { Stage, ErrorType, UpgradeError, BookingRef } from '../../interfaces';
import { StatusActions, DataActions, AuthActions, BasketActions } from '../../store/actions';
import { getBookingRef, getError, getJourneySummary } from '../../store/selectors';

@Component({
  selector: 'fin-upgrade-error',
  templateUrl: 'error.component.html',
})
export class ErrorComponent implements OnInit, OnDestroy {
  errorType: ErrorType = ErrorType.error;
  public readonly TripType = TripType;

  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly ErrorType = ErrorType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly UpgradeError = UpgradeError;

  subscriptions: Subscription = new Subscription();

  upgradeError$: Observable<UpgradeError>;
  bookingRef$: Observable<BookingRef>;
  journey$: Observable<Partial<BookingHeaderSummary>>;

  constructor(
    private store$: Store<AppState>,
    private route: ActivatedRoute,
    private router: Router,
    private configService: ConfigService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.bookingRef$ = this.store$.pipe(select(getBookingRef));
    this.journey$ = this.store$.pipe(
      select(getJourneySummary),
      map((journey) => {
        return {
          locations: journey,
          tripType: getTripTypeFromBookingLocationCodes(journey),
        };
      })
    );
    this.store$.dispatch(DataActions.clearPassengers());
    this.store$.dispatch(BasketActions.clearBasket());

    this.upgradeError$ = this.store$.pipe(
      select(getError),
      filter((error) => {
        if (!error || [UpgradeError.none, UpgradeError.httpError].includes(error)) {
          this.store$.dispatch(StatusActions.setStage({ stage: Stage.LOGIN }));
          return false;
        }

        return true;
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  getMMBViewPath(): Array<string> {
    return [this.languageService.langValue, RootPaths.MANAGE_BOOKING_ROOT, MANAGE_BOOKING_STEPS.VIEW.path];
  }

  get cta(): string {
    return this.configService.cfg.enableUpgradeTravelClass ? 'errors.booking.cta' : 'oneUpgrade.errors.maintenance.cta';
  }

  async onStartOver(): Promise<void> {
    this.subscriptions.add(
      this.store$
        .pipe(hasOrderData())
        .pipe(
          take(1),
          tap(async () => {
            if (this.errorType === ErrorType.maintenance) {
              try {
                const home = this.route.snapshot.root.firstChild.url[0].path;
                await this.router.navigate([`/${home}`]);
              } catch (_e) {
                await this.router.navigate(['/']);
              }
            }
          })
        )
        .subscribe((hasOrderDataValue) => {
          this.store$.dispatch(AuthActions.logout());

          if (hasOrderDataValue) {
            this.router.navigate(this.getMMBViewPath(), { replaceUrl: true });
          } else {
            this.store$.dispatch(StatusActions.setStage({ stage: Stage.LOGIN }));
          }
        })
    );
  }
}
